/**
 * Typed browser client for the development API. It knows only the transport contract; every decision
 * about safety, state or content lives on the server side of these calls.
 */
import { pcmStreamFrom, type SpokenAudio } from '../voice/spoken-audio.js';

export interface SourceCardDto {
  /** Why this source stands here, in the reply's voice; null when none passed the gates. */
  readonly relation: string | null;
  readonly kind: 'quran' | 'hadith';
  readonly provider: string;
  /**
   * The integrity fields. The API has sent these since `renderSourceCard` was written; the client
   * simply never declared them, so nothing could show a person why these words are trustworthy.
   */
  readonly providerContentId: string;
  readonly providerVersion: string;
  readonly retrievedAt: string;
  readonly expiresAt: string;
  readonly canonicalReference: string;
  readonly content: string;
  /** Verified against the approval before the card existed; used to mark the text for a reader. */
  readonly language: string;
  readonly translationOrExplanation: string | null;
  /**
   * The language a rendering of the meaning is written in, or null when the field is an explanation
   * in the body's own language. The renderer marks it, because a card holding Arabic beside English
   * holds two languages and marking only one lays the other out backwards.
   */
  readonly translationLanguage?: string | null;
  /** Whose rendering it is. Per-edition credit is a provider term and an honest one. */
  readonly translationEdition?: string | null;
  readonly grade: string | null;
  readonly attribution: string;
  readonly humanRecitationUrl: string | null;
  /** Decided by the server: render a player if and only if this is true. */
  readonly canPlayRecitation: boolean;
  readonly approvalId: string;
  readonly checksum: string;
}

export interface InteractionDto {
  readonly id: string;
  readonly type:
    'quick_choices' | 'understanding_check' | 'editable_plan' | 'priority_board' | 'commitment';
  readonly title: string;
  readonly options: readonly { readonly id: string; readonly label: string }[];
  readonly allowFreeText: true;
  readonly skippable: true;
}

export interface TurnResponseDto {
  readonly turnId: string;
  readonly assistantProse: string;
  /** Why the source belongs here, in the companion's words; null whenever no source renders. */
  readonly sourceLinkProse: string | null;
  readonly sourceCards: readonly SourceCardDto[];
  /** One approved interaction, or null. Optional in the type so a fixture need not name it. */
  readonly interaction?: InteractionDto | null;
  readonly safetyAction: 'none' | 'crisis_script' | 'decline_referral' | 'safe_fallback';
  /** Why a degraded turn degraded (closed machine code), or null on a normal turn. */
  readonly degradeReason: string | null;
  /** Whether the reply asks or answers. Absent on operations stored before the field existed. */
  readonly replyKind?: 'question' | 'statement';
  /** The question a question reply asks, lifted out so it can be shown on its own; null otherwise. */
  readonly question?: string | null;
  /** The trailing run of questions, in order, at most three; absent on older stored turns. */
  readonly questions?: readonly string[];
  /** What to tell the person when this turn did not answer normally; null when it did. */
  readonly notice: {
    readonly code: string;
    readonly text: string | null;
    readonly reviewPending: boolean;
    /** Ordinary wording to show while `text` is missing; null whenever `text` exists. */
    readonly interimText: string | null;
  } | null;
}

export interface ConversationEntryDto {
  readonly role: 'user' | 'assistant';
  readonly text: string;
  /** The reply's flow parts ride the read-back so a refresh loses nothing. */
  readonly sourceLinkProse?: string | null;
  readonly sourceCards?: readonly SourceCardDto[];
  /** An offer that vanished on refresh would be an offer withdrawn. */
  readonly interaction?: InteractionDto | null;
}

export interface ExploreResultDto {
  readonly kind: 'quran';
  readonly reference: string;
  readonly text: string;
  readonly provider: string;
  readonly attribution: string;
  readonly url: string | null;
  readonly reviewStatus: 'provider_direct';
}

export interface SessionSummaryDto {
  readonly id: string;
  readonly status: string;
  readonly questionCount: number;
  readonly createdAt: string;
  readonly preview: string;
}

export interface SessionDto {
  readonly id: string;
  readonly status: string;
  readonly questionCount: number;
  readonly createdAt: string;
}

export type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

export type ApiResult<T> =
  { readonly ok: true; readonly value: T } | { readonly ok: false; readonly code: string };

/** Allowlisted readiness metadata: mode names only, never a credential name and never a value. */
export interface ReadinessDto {
  readonly providerMode: string;
  readonly developmentIndicator: boolean;
  readonly modelWiring: string;
  /** What the running system says about the release its cards come from, or null when there is none. */
  readonly manifestApprovalClass: string | null;
  /** Kill switches, names and booleans only. Absent on responses that predate the field. */
  readonly capabilities?: readonly { readonly capability: string; readonly enabled: boolean }[];
  /** The doors this deployment offers, by name. Absent on responses that predate the field. */
  readonly signInMethods?: readonly string[];
  /** `email_code`: a mailed code is required to register; `no_verification`: no relay here, the account is made as given. */
  readonly registration?: string;
  /** Whether a forgotten password can be mailed here. Absent on responses that predate the field. */
  readonly passwordReset?: string;
}

/** What an account carries about the person (ADR-034): name, gender, and four closed statistics choices. */
export interface AccountProfileDto {
  readonly displayName: string;
  readonly gender: 'female' | 'male';
  readonly ageRange?: AgeRangeDto;
  readonly country?: CountryCodeDto;
  readonly referral?: ReferralDto;
  readonly interest?: InterestDto;
}
export type AgeRangeDto = '18-24' | '25-34' | '35-44' | '45-54' | '55+';
export type CountryCodeDto =
  | 'SA'
  | 'AE'
  | 'KW'
  | 'QA'
  | 'BH'
  | 'OM'
  | 'YE'
  | 'IQ'
  | 'SY'
  | 'LB'
  | 'JO'
  | 'PS'
  | 'EG'
  | 'SD'
  | 'LY'
  | 'TN'
  | 'DZ'
  | 'MA'
  | 'MR'
  | 'SO'
  | 'DJ'
  | 'KM'
  | 'other';
export type ReferralDto = 'tiktok' | 'instagram' | 'friend' | 'search' | 'other';
export type InterestDto = 'faith' | 'work' | 'family' | 'self' | 'other';

export interface OperationDto {
  readonly status: string;
  readonly response: TurnResponseDto | null;
  readonly errorCode: string | null;
  /** The pipeline stage genuinely running right now, or null once the work is over. */
  readonly stage: string | null;
  readonly retryAfterMs: number | null;
}

/**
 * The experience survey (founder, 2026-09-20). Wording comes from the server so every client asks
 * exactly the same two questions, and so changing them is a version rather than an edit.
 */
export interface SurveyTextDto {
  readonly ar: string;
  readonly en: string;
}

export type SurveyDueDto =
  | { readonly due: false }
  | {
      readonly due: true;
      readonly surveyVersion: string;
      readonly title: SurveyTextDto;
      readonly opinion: SurveyTextDto;
      readonly wouldPay: SurveyTextDto;
      readonly yesLabel: SurveyTextDto;
      readonly noLabel: SurveyTextDto;
      readonly opinionMaxLength: number;
    };

export interface ApiClient {
  readiness(): Promise<ApiResult<ReadinessDto>>;
  currentSession(): Promise<ApiResult<{ readonly authenticated: boolean }>>;
  signIn(inviteToken: string): Promise<ApiResult<{ readonly authenticationKind: string }>>;
  /** Step one of registration (ADR-034): a six-digit code goes to the address. */
  startRegistration(input: { readonly email: string }): Promise<
    ApiResult<{
      readonly status: string;
      readonly expiresAt: string;
      readonly resendAfterMs: number;
    }>
  >;
  /** A forgotten password, step one: a code to the address (same proof as registration). */
  startPasswordReset(input: { readonly email: string }): Promise<
    ApiResult<{
      readonly status: string;
      readonly expiresAt: string;
      readonly resendAfterMs: number;
    }>
  >;
  /** Step two: the code proves the mailbox and the password is replaced; every session ends. */
  resetPassword(input: {
    readonly email: string;
    readonly code: string;
    readonly password: string;
  }): Promise<ApiResult<{ readonly status: string }>>;
  /** Step two: the code, the password and the person's two details make the account. */
  register(input: {
    readonly email: string;
    readonly password: string;
    /** Empty when readiness reports `registration: no_verification`. */
    readonly code: string;
    /** The details are optional at the door (ADR-041); the web states them after it. */
    readonly displayName?: string;
    readonly gender?: 'female' | 'male';
    readonly ageRange?: AgeRangeDto;
    readonly country?: CountryCodeDto;
    readonly referral?: ReferralDto;
    readonly interest?: InterestDto;
    readonly adultAttested: boolean;
  }): Promise<ApiResult<{ readonly userId: string }>>;
  /** Signing in without a password (ADR-041), step one: a single-use link goes to the address. */
  startSignInLink(input: { readonly email: string; readonly adultAttested: boolean }): Promise<
    ApiResult<{
      readonly status: string;
      readonly expiresAt: string;
      readonly resendAfterMs: number;
    }>
  >;
  /** Step two: the token from the link's fragment, once. `created` says whether it made the account. */
  finishSignInLink(input: {
    readonly token: string;
    readonly locale: 'ar-SA' | 'en';
  }): Promise<ApiResult<{ readonly userId: string; readonly created: boolean }>>;
  login(input: {
    readonly email: string;
    readonly password: string;
  }): Promise<ApiResult<{ readonly userId: string }>>;
  logout(): Promise<ApiResult<null>>;
  /** Who this session is, the email of the account when it has one, and the person's two details once stated. */
  me(): Promise<
    ApiResult<{
      readonly userId: string;
      readonly email: string | null;
      readonly profile: AccountProfileDto | null;
    }>
  >;
  /** State or change the person's two details (ADR-034). */
  saveProfile(
    profile: AccountProfileDto,
  ): Promise<ApiResult<{ readonly profile: AccountProfileDto }>>;
  /** Account deletion (ADR-032): everything, immediately; the session ends with it. */
  deleteAccount(): Promise<ApiResult<{ readonly conversationsDeleted: number }>>;
  createSession(): Promise<ApiResult<SessionDto>>;
  /** Every conversation the caller has, newest first. */
  listSessions(): Promise<ApiResult<{ readonly sessions: readonly SessionSummaryDto[] }>>;
  /** Permanent erasure of one conversation and everything raised from its turns. */
  deleteSession(sessionId: string): Promise<ApiResult<null>>;
  /** Every conversation, and the memory raised from their turns. */
  eraseAllConversations(): Promise<ApiResult<{ conversationsDeleted: number }>>;
  /** The profile only: what Bilal remembers, leaving the conversations alone. */
  forgetProfile(): Promise<ApiResult<null>>;
  /** Direct provider search (Explore): zero generation, exact sources only. */
  exploreQuran(
    query: string,
  ): Promise<
    ApiResult<{ readonly totalFound: number; readonly results: readonly ExploreResultDto[] }>
  >;
  /**
   * The session this caller already has, if any. A refresh resumes it rather than starting another:
   * a new session would put the user back at zero questions with nothing having been refused.
   */
  resumeSession(): Promise<ApiResult<SessionDto>>;
  /** The session's words so far, from the server's in-process log — not durable storage. */
  conversation(
    sessionId: string,
  ): Promise<ApiResult<{ readonly entries: readonly ConversationEntryDto[] }>>;
  commitTurn(
    sessionId: string,
    input: TurnInput,
    clientTurnId: string,
    supersedesTurnId: string | null,
    depth?: 'quick' | 'balanced' | 'deep',
    /**
     * The language this person is reading in. It reaches the source resolvers and nothing else: a
     * reader who does not read Arabic is shown the verse with a named provider edition's rendering
     * of its meaning beside it.
     */
    readingLanguage?: 'ar' | 'en',
  ): Promise<ApiResult<{ readonly id: string }>>;
  pollOperation(operationId: string): Promise<ApiResult<OperationDto>>;
  /**
   * Asks the server to speak a turn it already delivered. The request names the turn and carries no
   * text: what is spoken is the server's own record of what it said, never anything from this page.
   */
  speakTurn(operationId: string, part?: 'prose' | 'link'): Promise<ApiResult<SpokenAudio>>;
  /** What the model asked to remember, pending per-item confirmation. Nothing here is a fact. */
  listMemoryProposals(): Promise<ApiResult<{ readonly proposals: readonly MemoryProposalDto[] }>>;
  /**
   * The consent act: one proposal becomes one fact. There is no bulk form deliberately — a
   * single click covering several delicate facts is not per-item consent.
   */
  confirmMemoryProposal(proposalId: string): Promise<ApiResult<MemoryFactDto>>;
  /** The visible profile: every confirmed fact, to be read, corrected and deleted. */
  listMemoryFacts(): Promise<ApiResult<{ readonly facts: readonly MemoryFactDto[] }>>;
  /** Whether this person owes an answer to the experience survey; the server decides, not a client. */
  surveyDue(sessionId: string): Promise<ApiResult<SurveyDueDto>>;
  answerSurvey(
    sessionId: string,
    input: {
      readonly surveyVersion: string;
      readonly opinion: string;
      readonly wouldPay: 'yes' | 'no';
    },
  ): Promise<ApiResult<null>>;
}

/**
 * What the person did with an offered interaction, sent as its own field (ADR-028).
 *
 * It used to be packed into `transcript` as a coded string. Identifiers only: every label is
 * resolved server-side against the offer it stored, so this page cannot describe a choice nobody
 * was offered. `edited` carries text because that text is the person's own words.
 */
export interface InteractionActDto {
  readonly interactionId: string;
  readonly kind: 'option_selected' | 'skipped' | 'sorted' | 'plan_committed';
  readonly optionId?: string;
  readonly placements?: readonly { readonly optionId: string; readonly bucket: string }[];
  readonly order?: readonly string[];
  readonly dropped?: readonly string[];
  readonly edited?: readonly { readonly optionId: string; readonly text: string }[];
}

/** A turn carries words, or an act on a control, and at least one of the two. */
export interface TurnInput {
  readonly transcript: string | null;
  readonly interaction: InteractionActDto | null;
}

export interface MemoryProposalDto {
  readonly id: string;
  readonly kind: string;
  readonly key: string;
  readonly value: string;
  readonly proposedAt: string;
  readonly expiresAt: string;
}

export interface MemoryFactDto {
  readonly id: string;
  readonly kind: string;
  readonly key: string;
  readonly value: string;
  readonly consentedAt: string;
}

async function readJson<T>(response: Response): Promise<ApiResult<T>> {
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const code =
      typeof body === 'object' && body !== null && 'code' in body
        ? String(Reflect.get(body, 'code'))
        : `http_${String(response.status)}`;
    return { ok: false, code };
  }
  return { ok: true, value: body as T };
}

export function createApiClient(baseUrl: string, fetchImpl: FetchLike): ApiClient {
  const send = async <T>(path: string, init?: RequestInit): Promise<ApiResult<T>> => {
    try {
      const response = await fetchImpl(`${baseUrl}${path}`, { credentials: 'include', ...init });
      return await readJson<T>(response);
    } catch {
      return { ok: false, code: 'network_unavailable' };
    }
  };
  const jsonInit = (
    method: string,
    body: unknown,
    headers: Record<string, string> = {},
  ): RequestInit => ({
    method,
    headers: { 'content-type': 'application/json', ...headers },
    body: JSON.stringify(body),
  });
  return {
    readiness: () => send<ReadinessDto>('/health/ready'),
    currentSession: () => send('/v1/development/session'),
    signIn: (inviteToken) => send('/v1/development/session', jsonInit('POST', { inviteToken })),
    startRegistration: (input) => send('/v1/auth/register/start', jsonInit('POST', input)),
    register: (input) => send('/v1/auth/register', jsonInit('POST', input)),
    startSignInLink: (input) => send('/v1/auth/link/start', jsonInit('POST', input)),
    finishSignInLink: (input) => send('/v1/auth/link/finish', jsonInit('POST', input)),
    startPasswordReset: (input) => send('/v1/auth/password/reset/start', jsonInit('POST', input)),
    resetPassword: (input) => send('/v1/auth/password/reset', jsonInit('POST', input)),
    login: (input) => send('/v1/auth/login', jsonInit('POST', input)),
    logout: async () => {
      try {
        const response = await fetchImpl(`${baseUrl}/v1/auth/logout`, {
          method: 'POST',
          credentials: 'include',
        });
        return response.ok || response.status === 204
          ? { ok: true, value: null }
          : { ok: false, code: 'logout_failed' };
      } catch {
        return { ok: false, code: 'network_error' };
      }
    },
    me: () => send('/v1/auth/me'),
    saveProfile: (profile) => send('/v1/account/profile', jsonInit('POST', profile)),
    deleteAccount: () => send('/v1/account/delete', { method: 'POST' }),
    createSession: () => send('/v1/sessions', { method: 'POST' }),
    listSessions: () => send('/v1/sessions'),
    deleteSession: (sessionId) => send(`/v1/sessions/${sessionId}`, { method: 'DELETE' }),
    eraseAllConversations: () => send('/v1/sessions', { method: 'DELETE' }),
    forgetProfile: () => send('/v1/memory', { method: 'DELETE' }),
    exploreQuran: (query) => send(`/v1/explore/quran?q=${encodeURIComponent(query)}`),
    resumeSession: () => send('/v1/sessions/current'),
    conversation: (sessionId) => send(`/v1/sessions/${sessionId}/conversation`),
    commitTurn: (sessionId, input, clientTurnId, supersedesTurnId, depth, readingLanguage) =>
      send(
        `/v1/sessions/${sessionId}/turns`,
        jsonInit(
          'POST',
          {
            // Omitted rather than sent as null: the contract requires one of the two to be
            // present, and a null transcript is not the same as no transcript.
            ...(input.transcript === null ? {} : { transcript: input.transcript }),
            ...(input.interaction === null ? {} : { interaction: input.interaction }),
            clientTurnId,
            supersedesTurnId,
            depth: depth ?? null,
            readingLanguage: readingLanguage ?? null,
          },
          { 'idempotency-key': `${clientTurnId}-idempotency` },
        ),
      ),
    pollOperation: (operationId) => send(`/v1/operations/${operationId}`),
    listMemoryProposals: () => send('/v1/memory/proposals'),
    confirmMemoryProposal: (proposalId) =>
      send(`/v1/memory/proposals/${proposalId}/confirm`, jsonInit('POST', {})),
    listMemoryFacts: () => send('/v1/memory/facts'),
    surveyDue: (sessionId) => send(`/v1/sessions/${sessionId}/survey`),
    answerSurvey: (sessionId, input) =>
      send(`/v1/sessions/${sessionId}/survey`, jsonInit('POST', input)),
    speakTurn: async (operationId, part) => {
      try {
        const response = await fetchImpl(`${baseUrl}/v1/operations/${operationId}/speech`, {
          credentials: 'include',
          method: 'POST',
          // A selector over what the server assembled — never text.
          ...(part === undefined
            ? {}
            : { headers: { 'content-type': 'application/json' }, body: JSON.stringify({ part }) }),
        });
        // A refusal is JSON with a code; audio is bytes. Nothing here reads the audio as text.
        if (!response.ok) return await readJson<SpokenAudio>(response);
        // A provider that streams is handed on as a stream, so the first word can be heard while
        // the last is still being made; anything else is one file, as before.
        return { ok: true, value: pcmStreamFrom(response) ?? (await response.blob()) };
      } catch {
        return { ok: false, code: 'network_unavailable' };
      }
    },
  };
}

/**
 * Polls an operation until it is terminal, reporting each truthful stage change as it happens.
 *
 * The server owns the cadence: it says `retryAfterMs` while the work runs and the loop obeys it.
 * The loop invents nothing — a stage is reported only when the server said it changed, and the
 * final result is returned exactly as the server recorded it. The wait is injected so tests run
 * without real time; the iteration cap only turns a hung operation into an explicit code instead
 * of a browser tab polling forever.
 */
export async function awaitOperation(
  poll: (operationId: string) => Promise<ApiResult<OperationDto>>,
  operationId: string,
  onStage: (stage: string | null) => void,
  wait: (ms: number) => Promise<void> = (ms) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    }),
): Promise<ApiResult<OperationDto>> {
  let reported: string | null = null;
  for (let attempt = 0; attempt < 600; attempt += 1) {
    const polled = await poll(operationId);
    if (!polled.ok) return polled;
    const operation = polled.value;
    if (operation.status !== 'running' && operation.status !== 'queued') return polled;
    if (operation.stage !== reported) {
      reported = operation.stage;
      onStage(operation.stage);
    }
    await wait(operation.retryAfterMs ?? 350);
  }
  return { ok: false, code: 'client_poll_timeout' };
}
